import React, { useEffect, useRef, useState } from 'react';
import { Smile } from 'lucide-react';
import { TEAL } from '../edit/editShared';
import { emojiCategoriesForPicker, emojiCategoryLabel, loadRecentEmojis, rememberRecentEmoji } from '../../utils/emojiCatalog';
import { currentLang } from '../../i18n';

/**
 * Emoji-Knopf links vom Eingabefeld (Marcel 21.10.2026, wie WhatsApp): öffnet ein Popover mit
 * Kategorie-Leiste und Raster; [onPick] fügt das Emoji in das Feld ein (Cursor-Position).
 * Schließt bei Klick außerhalb und mit Escape. Eine Stelle für Chats, Kommentare, Beiträge.
 */
const EmojiPickerButton: React.FC<{ onPick: (emoji: string) => void; disabled?: boolean; align?: 'left' | 'right'; up?: boolean; testId?: string }> = ({ onPick, disabled, align = 'left', up = true, testId = 'emoji-button' }) => {
  const [open, setOpen] = useState(false);
  const [recent, setRecent] = useState<string[]>([]);
  const [cat, setCat] = useState<string>('smileys');
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setRecent(loadRecentEmojis());
    const onDown = (ev: MouseEvent | TouchEvent) => { if (wrapRef.current && !wrapRef.current.contains(ev.target as Node)) setOpen(false); };
    const onKey = (ev: KeyboardEvent) => { if (ev.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('touchstart', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const cats = emojiCategoriesForPicker(recent);
  const active = cats.find((c) => c.key === cat) ?? cats[0];

  const pick = (emoji: string) => {
    onPick(emoji);
    rememberRecentEmoji(emoji);
    setRecent(loadRecentEmojis());
  };

  return (
    <div ref={wrapRef} className="relative shrink-0">
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault() /* Fokus im Textfeld lassen */}
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        aria-label="Emoji"
        aria-expanded={open}
        className="flex h-10 w-10 items-center justify-center rounded-full text-zinc-400 hover:bg-zinc-800 hover:text-white disabled:opacity-50"
        style={open ? { color: TEAL } : undefined}
        data-testid={testId}
      >
        <Smile size={20} />
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Emoji"
          className={`absolute z-[60] w-[320px] max-w-[calc(100vw-32px)] rounded-2xl border bg-black shadow-2xl ${up ? 'bottom-12' : 'top-12'} ${align === 'left' ? 'left-0' : 'right-0'}`}
          style={{ borderColor: `${TEAL}66` }}
          data-testid="emoji-panel"
        >
          <div className="flex gap-1 overflow-x-auto border-b border-zinc-800 px-2 py-1.5" data-testid="emoji-categories">
            {cats.map((c) => (
              <button
                key={c.key}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setCat(c.key)}
                aria-pressed={active?.key === c.key}
                title={emojiCategoryLabel(c, currentLang())}
                className={`flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-sm ${active?.key === c.key ? 'bg-[#3fced1]/20 text-white' : 'text-zinc-400 hover:bg-zinc-800'}`}
                data-testid={`emoji-cat-${c.key}`}
              >
                <span>{c.icon}</span>
                {active?.key === c.key && <span className="text-[12px] font-medium">{emojiCategoryLabel(c, currentLang())}</span>}
              </button>
            ))}
          </div>
          <div className="grid max-h-56 grid-cols-8 gap-0.5 overflow-y-auto p-2" data-testid={`emoji-grid-${active?.key ?? 'none'}`}>
            {(active?.emojis ?? []).map((e) => (
              <button key={e} type="button" onMouseDown={(ev) => ev.preventDefault()} onClick={() => pick(e)} className="flex h-9 w-9 items-center justify-center rounded-lg text-[22px] hover:bg-zinc-800" aria-label={e}>
                {e}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmojiPickerButton;
